import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";
import {
  getInvoiceMailLieferantUebersicht,
  lieferantEinvoiceStatusClass,
  lieferantEinvoiceStatusLabel,
  type InvoiceMailLieferantUebersichtItem,
  type InvoiceMailLieferantUebersichtResponse,
  type LieferantEinvoiceStatus,
} from "@/lib/invoiceMail";

type LieferantFilter = "followup" | "all" | LieferantEinvoiceStatus;

const FILTERS: Array<{ id: LieferantFilter; label: string }> = [
  { id: "followup", label: "Follow-up" },
  { id: "none", label: "Ohne" },
  { id: "partial", label: "Teilweise" },
  { id: "always", label: "E-Rechnung" },
  { id: "all", label: "Alle" },
];

function senderTitle(row: InvoiceMailLieferantUebersichtItem): string {
  return (row.lieferant_firmenname || row.from_name || row.sender_email || "Unbekannt").trim();
}

function senderSubtitle(row: InvoiceMailLieferantUebersichtItem): string {
  const title = senderTitle(row);
  const email = (row.sender_email || "").trim();
  if (email && email !== title) return email;
  const name = (row.from_name || "").trim();
  if (name && name !== title) return name;
  return "";
}

function matchesFilter(row: InvoiceMailLieferantUebersichtItem, filter: LieferantFilter): boolean {
  if (filter === "all") return true;
  if (filter === "followup") return row.einvoice_status !== "always";
  return row.einvoice_status === filter;
}

export function InvoiceMailLieferantOverview() {
  const [data, setData] = useState<InvoiceMailLieferantUebersichtResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<LieferantFilter>("followup");
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    void getInvoiceMailLieferantUebersicht()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const visibleItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (data?.items || []).filter((row) => {
      if (!matchesFilter(row, filter)) return false;
      if (!needle) return true;
      const haystack = `${senderTitle(row)} ${senderSubtitle(row)}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [data, filter, query]);

  function openWith(next: LieferantFilter) {
    setFilter(next);
    setQuery("");
    setOpen(true);
  }

  if (loading) {
    return <Spinner className="h-4 w-4 text-label-tertiary" />;
  }

  if (!data) return null;

  const { summary } = data;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5" aria-label="E-Rechnung nach Lieferant">
        <StatusBadge
          count={summary.always_einvoice}
          label="E-Rechnung"
          tone="green"
          onClick={() => openWith("always")}
        />
        <StatusBadge
          count={summary.partial_einvoice}
          label="teilweise"
          tone="orange"
          onClick={() => openWith("partial")}
        />
        <StatusBadge
          count={summary.no_einvoice}
          label="ohne"
          tone="red"
          onClick={() => openWith("none")}
        />
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        size="xl"
        title="E-Rechnung nach Lieferant"
        description={`${summary.needs_followup} Lieferanten brauchen Follow-up · ${summary.always_einvoice} senden durchgängig E-Rechnungen`}
        bodyClassName="!flex !min-h-0 !flex-col !overflow-hidden"
        panelClassName="sm:max-h-[min(40rem,calc(100dvh-2rem))]"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setFilter(item.id)}
                className={clsx(
                  "rounded-full px-3 py-1 text-xs font-medium",
                  filter === item.id
                    ? "bg-accent text-label-on-accent"
                    : "border border-separator text-label-secondary hover:bg-fill-quaternary/40",
                )}
              >
                {item.label}
              </button>
            ))}
            <input
              className="eh-input ml-auto h-8 w-full min-w-[12rem] flex-1 py-1 text-xs sm:max-w-xs"
              placeholder="Lieferant oder E-Mail suchen…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          {visibleItems.length === 0 ? (
            <p className="py-8 text-center text-sm text-label-secondary">
              {query.trim() ? "Kein Lieferant passt zur Suche." : "Keine Lieferanten in dieser Ansicht."}
            </p>
          ) : (
            <ul className="min-h-0 flex-1 divide-y divide-separator overflow-y-auto overscroll-contain rounded-xl border border-separator">
              {visibleItems.map((row) => {
                const subtitle = senderSubtitle(row);
                return (
                  <li key={row.sender_email} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-label">{senderTitle(row)}</div>
                      {subtitle ? <div className="truncate text-xs text-label-secondary">{subtitle}</div> : null}
                      {row.lieferant_gp_id ? (
                        <Link
                          to={`/kontakte/geschaeftspartner/${row.lieferant_gp_id}`}
                          className="text-xs text-accent hover:underline"
                        >
                          Geschäftspartner
                        </Link>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 font-semibold",
                          lieferantEinvoiceStatusClass(row.einvoice_status),
                        )}
                      >
                        {lieferantEinvoiceStatusLabel(row.einvoice_status)}
                      </span>
                      <span className="tabular-nums text-label-secondary">
                        {row.einvoice_mail_count}/{row.mail_count} E-Rechnungen
                        {row.pending_count > 0 ? ` · ${row.pending_count} offen` : ""}
                      </span>
                      <span className="text-label-tertiary">
                        {row.last_received_at ? fmtDateTime(row.last_received_at) : "—"}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Dialog>
    </>
  );
}

function StatusBadge({
  count,
  label,
  tone,
  onClick,
}: {
  count: number;
  label: string;
  tone: "green" | "orange" | "red";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition hover:brightness-110",
        tone === "green" && "bg-status-green/15 text-status-green",
        tone === "orange" && "bg-status-orange/15 text-status-orange",
        tone === "red" && "bg-status-red/15 text-status-red",
      )}
    >
      <span className="tabular-nums">{count}</span>
      {label}
    </button>
  );
}
